import { centsToDollars } from '@/api/boundaryAdapter';
import type {
  PuAiSummary,
  PuPatient,
  PuPractice,
  PuUnscheduledTreatment,
} from '@elva/ui';
import type {
  PublicUrgencyGateResponse,
  PublicUrgencyPageResponse,
  PublicUrgencyTreatment,
} from '../api/types';

function mapTreatment(treatment: PublicUrgencyTreatment): PuUnscheduledTreatment {
  return {
    id: treatment.id,
    title: treatment.title,
    subtitle: treatment.subtitle,
    code: treatment.code,
    toothNumber: treatment.tooth_number,
    ...(treatment.quadrant ? { quadrant: treatment.quadrant } : {}),
    video: treatment.video,
    fee: Math.round(centsToDollars(treatment.fee_cents)),
    insurance: Math.round(centsToDollars(treatment.insurance_cents)),
    patientEst: Math.round(centsToDollars(treatment.patient_est_cents)),
    urgencyTier: treatment.urgency_tier,
    urgencyLabel: treatment.urgency_label,
    whyMatters: treatment.why_matters,
    diagnosed: treatment.diagnosed,
    ...(treatment.doctor_note ? { doctorNote: treatment.doctor_note } : {}),
    bookingLink: treatment.booking_link,
  };
}

export function adaptPublicUrgencyGate(response: PublicUrgencyGateResponse): {
  practice: PuPractice;
  patient: PuPatient;
} {
  return {
    practice: {
      name: response.practice.name,
      logoMark: response.practice.logo_mark,
      tagline: response.practice.tagline,
      address: '',
      phone: response.practice.phone,
      bookingUrl: '',
      language: { current: 'English', available: ['English'] },
    },
    patient: {
      preferredName: response.patient.preferred_name,
      fullName: response.patient.full_name,
      surname: response.patient.surname,
      initials: response.patient.initials,
      dobMatch: '',
      unscheduledValue: 0,
      insurance: {
        carrier: '',
        annualMax: 0,
        used: 0,
        remaining: 0,
        resetDate: '',
        resetsInDays: 0,
        familyPool: false,
      },
    },
  };
}

export function adaptPublicUrgencyPage(response: PublicUrgencyPageResponse): {
  patient: PuPatient;
  practice: PuPractice;
  aiSummary: PuAiSummary;
  treatments: PuUnscheduledTreatment[];
} {
  return {
    patient: {
      preferredName: response.patient.preferred_name,
      fullName: response.patient.full_name,
      surname: response.patient.surname,
      initials: response.patient.initials,
      dobMatch: '',
      unscheduledValue: Math.round(centsToDollars(response.patient.unscheduled_value_cents)),
      insurance: {
        carrier: response.patient.insurance.carrier,
        annualMax: Math.round(centsToDollars(response.patient.insurance.annual_max_cents)),
        used: Math.round(centsToDollars(response.patient.insurance.used_cents)),
        remaining: Math.round(centsToDollars(response.patient.insurance.remaining_cents)),
        resetDate: response.patient.insurance.reset_date,
        resetsInDays: response.patient.insurance.resets_in_days,
        familyPool: response.patient.insurance.family_pool,
      },
    },
    practice: {
      name: response.practice.name,
      logoMark: response.practice.logo_mark,
      tagline: response.practice.tagline,
      address: response.practice.address,
      phone: response.practice.phone,
      bookingUrl: response.practice.booking_url,
      language: response.practice.language,
    },
    aiSummary: {
      generatedAt: response.ai_summary.generated_at,
      body: response.ai_summary.body,
      ...(response.ai_summary.callout ? { callout: response.ai_summary.callout } : {}),
    },
    treatments: response.treatments.map(mapTreatment),
  };
}
